/** Shared profession list for Coupons + Smart Bill (same source of truth). */

export const ALL_PROFESSION = 'ALL';
export const OTHER_PROFESSION = 'OTHER';

/** Built-in list — also used as fallback when /api/professions is unavailable. */
export const DEFAULT_PROFESSIONS = [
  'STUDENT',
  'JOBSEEKER',
  'FARMER',
  'PRIVATE EMPLOYEE',
  'GOVT EMPLOYEE',
  'RETIRED GOVT EMPLOYEE',
  'HOUSE WIFE',
  'AGRICULTURE LABOUR',
  'DRIVER',
];

export const normalizeProfessionName = (value) =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

/** Collapse spaces for duplicate detection: "JOB SEEKER" === "JOBSEEKER" */
export const professionKey = (value) =>
  normalizeProfessionName(value).replace(/\s+/g, '');

export const isSpecialProfession = (value) => {
  const n = normalizeProfessionName(value);
  return n === ALL_PROFESSION || n === OTHER_PROFESSION;
};

const defaultByKey = (() => {
  const map = new Map();
  DEFAULT_PROFESSIONS.forEach((name) => {
    map.set(professionKey(name), normalizeProfessionName(name));
  });
  return map;
})();

/**
 * Prefer canonical DEFAULT spelling when a space-variant exists
 * (e.g. JOB SEEKER -> JOBSEEKER).
 */
export const canonicalizeProfessionName = (raw) => {
  const name = normalizeProfessionName(raw);
  if (!name) return '';
  const key = professionKey(name);
  if (defaultByKey.has(key)) return defaultByKey.get(key);
  return name;
};

/**
 * Merge API professions with defaults; unique by collapsed key
 * (defaults first, then extras alphabetically).
 */
export const mergeProfessionLists = (apiNames = []) => {
  const byKey = new Map();

  const push = (raw, preferExisting = false) => {
    const canonical = canonicalizeProfessionName(raw);
    if (!canonical || isSpecialProfession(canonical)) return;
    const key = professionKey(canonical);
    if (byKey.has(key) && preferExisting) return;
    if (!byKey.has(key) || !preferExisting) {
      // Prefer default spelling when available
      byKey.set(key, defaultByKey.get(key) || canonical);
    }
  };

  DEFAULT_PROFESSIONS.forEach((n) => push(n, false));
  (Array.isArray(apiNames) ? apiNames : []).forEach((n) => push(n, true));

  const defaultsSet = new Set(DEFAULT_PROFESSIONS.map(professionKey));
  const base = DEFAULT_PROFESSIONS.filter((p) => byKey.has(professionKey(p)));
  const extras = [...byKey.entries()]
    .filter(([key]) => !defaultsSet.has(key))
    .map(([, name]) => name)
    .sort();
  return [...base, ...extras];
};

/**
 * Coupons: empty / ALL → no restriction (null).
 * Specific selections → uppercase list (excludes ALL/OTHER placeholders).
 */
export const toAllowedProfessionsPayload = (selected = [], otherText = '') => {
  const list = Array.isArray(selected) ? selected : [];
  const hasAll =
    list.length === 0 ||
    list.some((p) => normalizeProfessionName(p) === ALL_PROFESSION);

  if (hasAll) return null;

  const result = [];
  const seen = new Set();

  list.forEach((raw) => {
    const name = canonicalizeProfessionName(raw);
    if (!name || name === ALL_PROFESSION) return;
    if (normalizeProfessionName(raw) === OTHER_PROFESSION) {
      const custom = canonicalizeProfessionName(otherText);
      if (custom && !seen.has(professionKey(custom))) {
        seen.add(professionKey(custom));
        result.push(custom);
      }
      return;
    }
    if (!seen.has(professionKey(name))) {
      seen.add(professionKey(name));
      result.push(name);
    }
  });

  return result.length > 0 ? result : null;
};

/** Normalize stored allowed_professions for UI (empty → ALL selected). */
export const fromAllowedProfessionsPayload = (raw) => {
  let list = [];
  if (!raw) return [ALL_PROFESSION];
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch {
      list = [];
    }
  } else if (Array.isArray(raw)) {
    list = raw;
  }

  const cleaned = [];
  const seen = new Set();
  list.forEach((item) => {
    const name = canonicalizeProfessionName(item);
    if (!name || name === ALL_PROFESSION) return;
    const key = professionKey(name);
    if (seen.has(key)) return;
    seen.add(key);
    cleaned.push(name);
  });

  return cleaned.length > 0 ? cleaned : [ALL_PROFESSION];
};

/**
 * Smart Bill customer profession value for save.
 * ALL / empty → '' (optional). OTHER → custom text. Else selected name.
 */
export const resolveCustomerProfessionValue = (
  selectedValue,
  otherText = '',
) => {
  const selected = normalizeProfessionName(selectedValue);
  if (!selected || selected === ALL_PROFESSION) return '';
  if (selected === OTHER_PROFESSION) {
    return canonicalizeProfessionName(otherText);
  }
  return canonicalizeProfessionName(selected);
};

/** Map a stored customer profession back to select + other text. */
export const splitCustomerProfessionValue = (stored, options = []) => {
  const value = canonicalizeProfessionName(stored);
  if (!value || value === ALL_PROFESSION) {
    return { selectValue: ALL_PROFESSION, otherText: '' };
  }
  const optionKeys = new Set(
    (options.length ? options : DEFAULT_PROFESSIONS).map(professionKey),
  );
  if (optionKeys.has(professionKey(value))) {
    return { selectValue: value, otherText: '' };
  }
  return { selectValue: OTHER_PROFESSION, otherText: value };
};
